import { NodeType } from '../types.js';

// Remove direct API key usage - now using backend API
const BACKEND_API_BASE = process.env.REACT_APP_BACKEND_URL || '/api';

// Add custom node types to availableNodeTypes array
const availableNodeTypes = [...Object.values(NodeType), "NOVEL_NODE", "TODO_LIST", "TODO_VIEW", "DOCUMENT_NODE", "DELEGATION_NODE"];

const enhancedWorkflowSchema = {
  // ... existing schema definition ...
  type: Object,
  properties: {
    nodes: {
      type: "array",
      items: {
        type: "object",
        properties: {
          type: {
            type: "string",
            enum: [...availableNodeTypes, "NOVEL_NODE", "TODO_LIST", "TODO_VIEW", "DOCUMENT_NODE", "DELEGATION_NODE", "COMMENT"],
            description: 'The type of the workflow node.',
          },
          label: {
            type: "string",
            description: 'A concise, user-friendly label for what this node does.',
          },
          assignedTo: {
            type: "string",
            description: 'Role or person responsible for this task (e.g., "Founder", "Legal Team", "Marketing Manager").',
          },
          priority: {
            type: "string",
            enum: ["LOW", "MEDIUM", "HIGH"],
            description: 'Priority level of the task.',
          },
          estimatedDuration: {
            type: "string",
            description: 'Estimated time to complete this task (e.g., "2 days", "1 week").',
          },
          requiredDocuments: {
            type: "array",
            items: {
              type: "string",
            },
            description: 'List of documents required to complete this task.',
          },
          todoItems: {
            type: "array",
            items: {
              type: "object",
              properties: {
                id: { type: "string" },
                text: { type: "string" },
                completed: { type: "boolean" },
                assignedTo: { type: "string" },
                deadline: { type: "string" },
              },
              required: ["id", "text", "completed"],
            },
            description: 'List of specific todo items for this task.',
          },
          successCriteria: {
            type: "string",
            description: 'How to determine if this task was completed successfully.',
          },
        },
        required: ["type", "label"],
      },
    },
    projectMetadata: {
      type: "object",
      properties: {
        title: { type: "string" },
        description: { type: "string" },
        timeline: { type: "string" },
        budget: { type: "string" },
        industry: { type: "string" },
        targetMarket: { type: "string" },
      },
      description: 'Metadata about the overall project.',
    },
  },
  required: ["nodes"],
};

/**
 * Generates a workflow from a user prompt using the backend API
 * @param {string} prompt - The user's business idea or description
 * @param {Function} onNodeReceived - Callback for streaming node updates
 * @param {string} authToken - Google OAuth token for authentication
 * @returns {Promise<Object>} The generated workflow
 */
async function* generateWorkflowFromPrompt(prompt, onNodeReceived = null, authToken = null) {
  try {
    // Security: Send request to backend instead of using Gemini API directly
    const headers = {
      'Content-Type': 'application/json',
    };
    
    // Add authorization token if provided
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }
    
    const response = await fetch(`${BACKEND_API_BASE}/generate-business-plan-public`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ 
        prompt: prompt,
        max_nodes: 20
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    // Parse the response from the backend
    let jsonString = data.plan;
    
    // Clean up the response to extract JSON
    if (jsonString.includes('```json')) {
      jsonString = jsonString.split('```json')[1].split('```')[0];
    } else if (jsonString.includes('```')) {
      jsonString = jsonString.split('```')[1].split('```')[0];
    }
    
    // Parse the JSON response
    const parsedResponse = JSON.parse(jsonString.trim());

    if (!parsedResponse.nodes || !Array.isArray(parsedResponse.nodes)) {
      throw new Error("Invalid response format from backend");
    }

    // Process nodes and yield them one by one for streaming
    for (const item of parsedResponse.nodes) {
      const baseNode = {
        id: item.id || `node_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
        type: item.type || NodeType.PROCESS,
        position: { x: Math.random() * 400, y: Math.random() * 400 },
        data: {
          label: item.label || item.name || 'New Task',
          assignedTo: item.assignedTo || '',
          priority: item.priority || 'MEDIUM',
          estimatedDuration: item.estimatedDuration || '',
          requiredDocuments: item.requiredDocuments || [],
          successCriteria: item.successCriteria || '',
          todoItems: item.todoItems || [],
        }
      };

      // Handle different node types with specific data structures
      if (item.type === "TODO_LIST") {
        if (item.todoItems && Array.isArray(item.todoItems)) {
          baseNode.data.items = item.todoItems.map(todo => ({
            id: todo.id || `todo_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
            text: todo.text || '',
            completed: todo.completed || false,
            assignedTo: todo.assignedTo || '',
            deadline: todo.deadline || ''
          }));
        } else {
          baseNode.data.items = [];
        }
      } else if (item.assignedTo) {
        baseNode.data.assignedTo = item.assignedTo;
        baseNode.data.priority = item.priority || "MEDIUM";
        baseNode.data.estimatedDuration = item.estimatedDuration || "";
        baseNode.data.requiredDocuments = item.requiredDocuments || [];
        baseNode.data.todoItems = item.todoItems || [];
        baseNode.data.successCriteria = item.successCriteria || "";
      }
      
      yield baseNode;
      
      // Call the callback if provided
      if (onNodeReceived) {
        onNodeReceived(baseNode);
      }
      
      // Small delay to simulate streaming
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    return {
      metadata: parsedResponse.projectMetadata || {},
    };
  } catch (error) {
    console.error("Error generating workflow from prompt:", error);
    // Provide more detailed error information
    if (error.message.includes("API key")) {
      throw new Error("Invalid API key. Please check your backend API configuration.");
    } else if (error.message.includes("quota")) {
      throw new Error("API quota exceeded. Please try again later or check your Gemini API plan.");
    } else if (error.message.includes("parse")) {
      throw new Error("Failed to parse AI response. The response format was unexpected.");
    } else if (error.message.includes("overloaded") || error.message.includes("UNAVAILABLE")) {
      throw new Error("The AI service is currently overloaded. Please try again in a few minutes.");
    } else if (error.message.includes("timeout")) {
      throw new Error("The request timed out. Please try again.");
    } else if (error.message.includes("network") || error.message.includes("Failed to fetch")) {
      throw new Error("Network error. Please check your internet connection and try again.");
    } else {
      throw new Error(`Failed to generate workflow: ${error.message}`);
    }
  }
}

/**
 * Generates a workflow from a user prompt (non-streaming version)
 * @param {string} prompt - The user's business idea or description
 * @param {string} authToken - Google OAuth token for authentication
 * @returns {Promise<Object>} The generated workflow
 */
async function generateWorkflowFromPromptBatch(prompt, authToken = null) {
  try {
    // Security: Send request to backend instead of using Gemini API directly
    const headers = {
      'Content-Type': 'application/json',
    };
    
    // Add authorization token if provided
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }
    
    const response = await fetch(`${BACKEND_API_BASE}/generate-business-plan-public`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ 
        prompt: prompt,
        max_nodes: 20
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    // Parse the response from the backend
    let jsonString = data.plan;
    
    // Clean up the response to extract JSON
    if (jsonString.includes('```json')) {
      jsonString = jsonString.split('```json')[1].split('```')[0];
    } else if (jsonString.includes('```')) {
      jsonString = jsonString.split('```')[1].split('```')[0];
    }
    
    // Parse the JSON response
    const parsedResponse = JSON.parse(jsonString.trim());

    if (!parsedResponse.nodes || !Array.isArray(parsedResponse.nodes)) {
      throw new Error("Invalid response format from backend");
    }

    // Process all nodes at once
    const processedNodes = parsedResponse.nodes.map(item => {
      const baseNode = {
        id: item.id || `node_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
        type: item.type || NodeType.PROCESS,
        position: { x: Math.random() * 400, y: Math.random() * 400 },
        data: {
          label: item.label || item.name || 'New Task',
          assignedTo: item.assignedTo || '',
          priority: item.priority || 'MEDIUM',
          estimatedDuration: item.estimatedDuration || '',
          requiredDocuments: item.requiredDocuments || [],
          successCriteria: item.successCriteria || '',
          todoItems: item.todoItems || [],
        }
      };

      // Handle different node types with specific data structures
      if (item.type === "TODO_LIST") {
        if (item.todoItems && Array.isArray(item.todoItems)) {
          baseNode.data.items = item.todoItems.map(todo => ({
            id: todo.id || `todo_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
            text: todo.text || '',
            completed: todo.completed || false,
            assignedTo: todo.assignedTo || '',
            deadline: todo.deadline || ''
          }));
        } else {
          baseNode.data.items = [];
        }
      } else if (item.assignedTo) {
        baseNode.data.assignedTo = item.assignedTo;
        baseNode.data.priority = item.priority || "MEDIUM";
        baseNode.data.estimatedDuration = item.estimatedDuration || "";
        baseNode.data.requiredDocuments = item.requiredDocuments || [];
        baseNode.data.todoItems = item.todoItems || [];
        baseNode.data.successCriteria = item.successCriteria || "";
      }
      
      return baseNode;
    });

    return {
      nodes: processedNodes,
      metadata: parsedResponse.projectMetadata || {},
    };
  } catch (error) {
    console.error("Error generating workflow from prompt:", error);
    // Provide more detailed error information
    if (error.message.includes("API key")) {
      throw new Error("Invalid API key. Please check your backend API configuration.");
    } else if (error.message.includes("quota")) {
      throw new Error("API quota exceeded. Please try again later or check your Gemini API plan.");
    } else if (error.message.includes("parse")) {
      throw new Error("Failed to parse AI response. The response format was unexpected.");
    } else if (error.message.includes("overloaded") || error.message.includes("UNAVAILABLE")) {
      throw new Error("The AI service is currently overloaded. Please try again in a few minutes.");
    } else if (error.message.includes("timeout")) {
      throw new Error("The request timed out. Please try again.");
    } else if (error.message.includes("network") || error.message.includes("Failed to fetch")) {
      throw new Error("Network error. Please check your internet connection and try again.");
    } else {
      throw new Error(`Failed to generate workflow: ${error.message}`);
    }
  }
}

/**
 * Generates a single node from a prompt
 * @param {string} prompt - The prompt to generate a node from
 * @returns {Promise<Object>} The generated node
 */
async function generateNodeFromPrompt(prompt) {
  // For now, return a basic node - in a real implementation you'd have a specific endpoint
  // This is a simplified version that doesn't use AI for single node generation
  return {
    id: `node_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
    type: NodeType.PROCESS,
    position: { x: Math.random() * 400, y: Math.random() * 400 },
    data: {
      label: prompt || 'New Task',
      assignedTo: '',
      priority: 'MEDIUM',
      estimatedDuration: '',
      requiredDocuments: [],
      successCriteria: '',
      todoItems: [],
    }
  };
}

// Stream workflow function for real-time node generation
async function* streamWorkflowFromPrompt(prompt, onNodeReceived = null) {
  try {
    // Security: Send request to backend instead of using Gemini API directly
    const response = await fetch(`${BACKEND_API_BASE}/generate-business-plan-public`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': process.env.REACT_APP_SERVER_API_KEY || 'fallback-key', // Use a fallback for dev
      },
      body: JSON.stringify({ 
        prompt: prompt,
        max_nodes: 20
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    // Parse the response from the backend
    let jsonString = data.plan;
    
    // Clean up the response to extract JSON
    if (jsonString.includes('```json')) {
      jsonString = jsonString.split('```json')[1].split('```')[0];
    } else if (jsonString.includes('```')) {
      jsonString = jsonString.split('```')[1].split('```')[0];
    }
    
    // Parse the JSON response
    const parsedResponse = JSON.parse(jsonString.trim());

    if (!parsedResponse.nodes || !Array.isArray(parsedResponse.nodes)) {
      throw new Error("Invalid response format from backend");
    }

    // Process nodes and yield them one by one for streaming
    for (const item of parsedResponse.nodes) {
      const baseNode = {
        id: item.id || `node_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
        type: item.type || NodeType.PROCESS,
        position: { x: Math.random() * 400, y: Math.random() * 400 },
        data: {
          label: item.label || item.name || 'New Task',
          assignedTo: item.assignedTo || '',
          priority: item.priority || 'MEDIUM',
          estimatedDuration: item.estimatedDuration || '',
          requiredDocuments: item.requiredDocuments || [],
          successCriteria: item.successCriteria || '',
          todoItems: item.todoItems || [],
        }
      };

      // Handle different node types with specific data structures
      if (item.type === "TODO_LIST") {
        if (item.todoItems && Array.isArray(item.todoItems)) {
          baseNode.data.items = item.todoItems.map(todo => ({
            id: todo.id || `todo_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
            text: todo.text || '',
            completed: todo.completed || false,
            assignedTo: todo.assignedTo || '',
            deadline: todo.deadline || ''
          }));
        } else {
          baseNode.data.items = [];
        }
      } else if (item.assignedTo) {
        baseNode.data.assignedTo = item.assignedTo;
        baseNode.data.priority = item.priority || "MEDIUM";
        baseNode.data.estimatedDuration = item.estimatedDuration || "";
        baseNode.data.requiredDocuments = item.requiredDocuments || [];
        baseNode.data.todoItems = item.todoItems || [];
        baseNode.data.successCriteria = item.successCriteria || "";
      }
      
      yield baseNode;
      
      // Call the callback if provided
      if (onNodeReceived) {
        onNodeReceived(baseNode);
      }
      
      // Small delay to simulate streaming
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    return {
      metadata: parsedResponse.projectMetadata || {},
    };
  } catch (error) {
    console.error("Error generating workflow from prompt:", error);
    // Provide more detailed error information
    if (error.message.includes("API key")) {
      throw new Error("Invalid API key. Please check your backend API configuration.");
    } else if (error.message.includes("quota")) {
      throw new Error("API quota exceeded. Please try again later or check your Gemini API plan.");
    } else if (error.message.includes("parse")) {
      throw new Error("Failed to parse AI response. The response format was unexpected.");
    } else if (error.message.includes("overloaded") || error.message.includes("UNAVAILABLE")) {
      throw new Error("The AI service is currently overloaded. Please try again in a few minutes.");
    } else if (error.message.includes("timeout")) {
      throw new Error("The request timed out. Please try again.");
    } else if (error.message.includes("network") || error.message.includes("Failed to fetch")) {
      throw new Error("Network error. Please check your internet connection and try again.");
    } else {
      throw new Error(`Failed to generate workflow: ${error.message}`);
    }
  }
}

export { 
  generateWorkflowFromPrompt, 
  generateWorkflowFromPromptBatch, 
  streamWorkflowFromPrompt,
  generateNodeFromPrompt,
  enhancedWorkflowSchema 
};